import React, { useEffect, useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Btn, Loader, EmptyState, Field, TextInput, TextArea, SelectInput } from "../components/Primitives";
import { useAuth } from "../lib/auth";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";

const STATUS_COLORS = {
  not_implemented: { bg: "var(--danger-soft)", color: "var(--danger)" },
  partial: { bg: "var(--warn-soft)", color: "var(--warn)" },
  implemented: { bg: "var(--success-soft)", color: "var(--success)" },
  verified: { bg: "var(--success-soft)", color: "var(--success)" },
};
const RISK_COLORS = {
  low: { bg: "var(--success-soft)", color: "var(--success)" },
  medium: { bg: "var(--warn-soft)", color: "var(--warn)" },
  high: { bg: "var(--danger-soft)", color: "var(--danger)" },
  critical: { bg: "var(--danger-soft)", color: "var(--danger)" },
};

function Pill({ label, mapObj }) {
  const t = mapObj?.[label] || { bg: "var(--bg-2)", color: "var(--muted)" };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-[3px] text-[10.5px] font-medium rounded-md"
      style={{ background: t.bg, color: t.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
      {label?.replace(/_/g, " ")}
    </span>
  );
}

function StatCard({ label, value, tone = "primary", testId, hint }) {
  const c = tone === "danger" ? "var(--danger)" : tone === "success" ? "var(--success)"
          : tone === "warn" ? "var(--warn)" : "var(--primary)";
  return (
    <div className="surface rounded-xl p-4" style={{ border: "1px solid var(--border)" }}>
      <div className="text-[11px] font-semibold uppercase tracking-wider muted">{label}</div>
      <div data-testid={testId} className="text-2xl font-semibold mt-1" style={{ color: c }}>{value}</div>
      {hint && <div className="text-[11px] muted mt-1">{hint}</div>}
    </div>
  );
}

const emptyCtrl = {
  name: "", control_type: "encryption_at_rest", description: "",
  owner: "", applicable: true, status: "not_implemented", risk_rating: "medium",
  last_tested_date: "", next_review_date: "", gaps: "", dpdp_ref: "Sec. 8(5)",
};

const CONTROL_TYPES = ["encryption_at_rest", "encryption_in_transit", "masking_tokenization", "access_control", "mfa", "least_privilege", "logging", "monitoring", "backup", "disaster_recovery", "vulnerability_management", "incident_response", "vendor_security_clauses", "data_loss_prevention", "key_management", "audit_logging", "secure_deletion", "security_testing"];

export default function SecurityControls() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "org_admin", "security_officer", "dpo"].includes(user?.role);
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [ctrlModal, setCtrlModal] = useState(false);
  const [ctrlForm, setCtrlForm] = useState(emptyCtrl);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/security-safeguards/dashboard");
      setDash(data);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const seedBaseline = async () => {
    try {
      const { data } = await api.post("/security-safeguards/seed-baseline");
      toast.success(`Baseline seeded: ${data.inserted} new controls`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const assess = async () => {
    try {
      const payload = {
        status: ctrlForm.status,
        owner: ctrlForm.owner,
        gaps: ctrlForm.gaps,
        last_tested_date: ctrlForm.last_tested_date || null,
        next_review_date: ctrlForm.next_review_date || null,
        risk_rating: ctrlForm.risk_rating,
      };
      if (editing) {
        await api.post(`/security-safeguards/${editing.id}/assess`, payload);
        toast.success("Assessment saved");
      } else {
        // Create control via generic CRUD
        await api.post("/security-controls", ctrlForm);
        toast.success("Control created");
      }
      setCtrlModal(false); setEditing(null); setCtrlForm(emptyCtrl);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const openAssess = (c) => { setEditing(c); setCtrlForm({ ...emptyCtrl, ...c }); setCtrlModal(true); };
  const openNew = () => { setEditing(null); setCtrlForm(emptyCtrl); setCtrlModal(true); };

  const controls = dash?.controls || [];
  const filtered = useMemo(() => {
    if (!filter) return controls;
    return controls.filter(c => c.status === filter);
  }, [controls, filter]);

  if (loading && !dash) return <Loader />;

  return (
    <div>
      <PageHeader
        title="Security Safeguards"
        kicker="DPDP Sec. 8(5) Reasonable Safeguards"
        description="Track technical & organizational controls, evidence and coverage against DPDP obligations."
        testId="sec-page-header"
        actions={
          <>
            <Btn variant="secondary" onClick={load} data-testid="sec-refresh"><Icons.RefreshCw className="w-3.5 h-3.5" /> Refresh</Btn>
            {isAdmin && (
              <Btn variant="secondary" onClick={seedBaseline} data-testid="sec-seed">
                <Icons.Shield className="w-3.5 h-3.5" /> Seed baseline
              </Btn>
            )}
            {isAdmin && tab === "controls" && (
              <Btn onClick={openNew} data-testid="sec-new-control"><Icons.Plus className="w-3.5 h-3.5" /> New control</Btn>
            )}
          </>
        }
      />

      <div className="mb-5 flex items-center gap-1 surface rounded-lg p-1 w-fit" style={{ border: "1px solid var(--border)" }}>
        {[
          { key: "dashboard", label: "Dashboard", icon: "LayoutDashboard" },
          { key: "controls", label: "Controls", icon: "Shield" },
          { key: "gaps", label: "Gaps & reviews", icon: "AlertTriangle" },
        ].map((t) => {
          const Icon = Icons[t.icon] || Icons.Circle;
          return (
            <button key={t.key} data-testid={`sec-tab-${t.key}`} onClick={() => setTab(t.key)}
              className="px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5"
              style={{
                background: tab === t.key ? "var(--primary-soft)" : "transparent",
                color: tab === t.key ? "var(--primary)" : "var(--muted)",
              }}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dashboard" && dash && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <StatCard label="Coverage score" value={`${dash.score}%`} tone={dash.score >= 80 ? "success" : dash.score >= 50 ? "warn" : "danger"} testId="stat-score" hint="Weighted implementation coverage" />
            <StatCard label="Total controls" value={dash.total_controls} testId="stat-total" />
            <StatCard label="Verified" value={dash.by_status?.verified || 0} tone="success" testId="stat-verified" />
            <StatCard label="Not implemented" value={dash.by_status?.not_implemented || 0} tone="danger" testId="stat-notimpl" />
            <StatCard label="Partial" value={dash.by_status?.partial || 0} tone="warn" testId="stat-partial" />
            <StatCard label="Missing baseline" value={dash.missing_baseline?.length || 0} tone="warn" testId="stat-missing" hint="Seed baseline to add" />
            <StatCard label="Overdue reviews" value={dash.overdue_review?.length || 0} tone="danger" testId="stat-overdue" />
            <StatCard label="Critical risk" value={dash.by_risk?.critical || 0} tone="danger" testId="stat-critical" />
          </div>

          <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
            <div className="text-[13px] font-semibold ink mb-3">Coverage by control type</div>
            <div className="space-y-2" data-testid="by-type-list">
              {Object.entries(dash.by_type || {}).map(([type, stats]) => {
                const pct = stats.total ? Math.round((stats.implemented / stats.total) * 100) : 0;
                return (
                  <div key={type}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex-1 text-[12px] ink">{type.replace(/_/g, " ")}</div>
                      <div className="text-[11px] muted">{stats.implemented} / {stats.total}</div>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--bg-2)" }}>
                      <div className="h-full" style={{ width: `${pct}%`, background: pct >= 80 ? "var(--success)" : pct >= 50 ? "var(--warn)" : "var(--danger)" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {tab === "controls" && (
        <>
          <div className="flex items-center gap-2 mb-4">
            <SelectInput data-testid="sec-status-filter" value={filter} onChange={(e) => setFilter(e.target.value)} style={{ maxWidth: 220 }}>
              <option value="">All statuses</option>
              {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </SelectInput>
          </div>
          {filtered.length === 0 ? (
            <EmptyState title="No controls" hint="Click Seed baseline to load the 18 DPDP Sec. 8(5) baseline controls." />
          ) : (
            <div className="surface rounded-xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
              <table className="w-full text-sm">
                <thead style={{ background: "var(--bg-2)" }}>
                  <tr>
                    {["Control", "Type", "Owner", "Status", "Risk", "Last tested", "Actions"].map(h => (
                      <th key={h} className="text-left px-4 py-2 text-[11px] font-semibold uppercase tracking-wider muted">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody data-testid="controls-tbody">
                  {filtered.map(c => (
                    <tr key={c.id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`ctrl-${c.id}`}>
                      <td className="px-4 py-2 ink text-[12px]">
                        <div className="font-medium">{c.name}</div>
                        {c.dpdp_ref && <div className="text-[10px] mono muted mt-0.5">{c.dpdp_ref}</div>}
                      </td>
                      <td className="px-4 py-2 muted text-[11.5px]">{c.control_type?.replace(/_/g, " ")}</td>
                      <td className="px-4 py-2 muted text-[11.5px]">{c.owner || "—"}</td>
                      <td className="px-4 py-2"><Pill label={c.status} mapObj={STATUS_COLORS} /></td>
                      <td className="px-4 py-2"><Pill label={c.risk_rating} mapObj={RISK_COLORS} /></td>
                      <td className="px-4 py-2 muted text-[11px]">{c.last_tested_date || "—"}</td>
                      <td className="px-4 py-2 text-right">
                        {isAdmin && (
                          <button onClick={() => openAssess(c)} className="text-[11px] font-medium hover:underline"
                            style={{ color: "var(--primary)" }} data-testid={`ctrl-assess-${c.id}`}>Assess</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "gaps" && dash && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
            <div className="text-[13px] font-semibold ink mb-3">Missing baseline controls</div>
            {(dash.missing_baseline || []).length === 0 ? (
              <div className="text-[12px] muted">All 18 baseline controls exist ✅</div>
            ) : (
              <div className="space-y-2" data-testid="missing-list">
                {dash.missing_baseline.map(m => (
                  <div key={m.control_type} className="p-3 rounded-md" style={{ background: "var(--warn-soft)" }}>
                    <div className="text-[12px] font-medium ink">{m.name}</div>
                    <div className="text-[10.5px] muted mt-1">{m.description}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
            <div className="text-[13px] font-semibold ink mb-3">Overdue reviews</div>
            {(dash.overdue_review || []).length === 0 ? (
              <div className="text-[12px] muted">No overdue reviews.</div>
            ) : (
              <div className="space-y-2" data-testid="overdue-list">
                {dash.overdue_review.map(c => (
                  <div key={c.id} className="p-3 rounded-md flex items-center justify-between" style={{ border: "1px solid var(--border)" }}>
                    <div>
                      <div className="text-[12px] font-medium ink">{c.name}</div>
                      <div className="text-[10.5px] muted">Due: {c.next_review_date}</div>
                    </div>
                    {isAdmin && (
                      <Btn variant="secondary" size="sm" onClick={() => openAssess(c)}>Review</Btn>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <Dialog open={ctrlModal} onOpenChange={setCtrlModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? `Assess: ${editing.name}` : "New security control"}</DialogTitle>
            <DialogDescription>{editing ? "Update status, evidence, and next-review dates." : "Add a technical or organizational control."}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[62vh] overflow-y-auto">
            {!editing && (
              <>
                <Field label="Name" required className="col-span-2"><TextInput data-testid="ctrl-name" value={ctrlForm.name} onChange={(e) => setCtrlForm({ ...ctrlForm, name: e.target.value })} /></Field>
                <Field label="Control type" required>
                  <SelectInput data-testid="ctrl-type" value={ctrlForm.control_type} onChange={(e) => setCtrlForm({ ...ctrlForm, control_type: e.target.value })}>
                    {CONTROL_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                  </SelectInput>
                </Field>
                <Field label="Description" className="col-span-2"><TextArea data-testid="ctrl-desc" value={ctrlForm.description} onChange={(e) => setCtrlForm({ ...ctrlForm, description: e.target.value })} /></Field>
              </>
            )}
            <Field label="Owner"><TextInput data-testid="ctrl-owner" value={ctrlForm.owner} onChange={(e) => setCtrlForm({ ...ctrlForm, owner: e.target.value })} /></Field>
            <Field label="Status">
              <SelectInput data-testid="ctrl-status" value={ctrlForm.status} onChange={(e) => setCtrlForm({ ...ctrlForm, status: e.target.value })}>
                {["not_implemented", "partial", "implemented", "verified"].map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </SelectInput>
            </Field>
            <Field label="Risk rating">
              <SelectInput data-testid="ctrl-risk" value={ctrlForm.risk_rating} onChange={(e) => setCtrlForm({ ...ctrlForm, risk_rating: e.target.value })}>
                {["low", "medium", "high", "critical"].map(r => <option key={r} value={r}>{r}</option>)}
              </SelectInput>
            </Field>
            <Field label="Last tested (YYYY-MM-DD)"><TextInput data-testid="ctrl-last" value={ctrlForm.last_tested_date} onChange={(e) => setCtrlForm({ ...ctrlForm, last_tested_date: e.target.value })} /></Field>
            <Field label="Next review (YYYY-MM-DD)"><TextInput data-testid="ctrl-next" value={ctrlForm.next_review_date} onChange={(e) => setCtrlForm({ ...ctrlForm, next_review_date: e.target.value })} /></Field>
            <Field label="Gaps" className="col-span-2"><TextArea data-testid="ctrl-gaps" value={ctrlForm.gaps} onChange={(e) => setCtrlForm({ ...ctrlForm, gaps: e.target.value })} placeholder="What is still missing?" /></Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setCtrlModal(false)}>Cancel</Btn>
            <Btn onClick={assess} data-testid="ctrl-save">{editing ? "Save assessment" : "Create control"}</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
